import { Box, Typography, useTheme } from '@mui/material'
import React from 'react'
import CustomTooltip from './CustomTooltip'
import { Icons } from '../../utils/utilities'

const CustomChip = ( { selected, containerStyles = {}, chipStyles = {}, textStyles = {}, tooltipPlacement = "top", textVariant, startIcon, endIcon, color, value, onClick, variant = "outlined", tooltipTitle = "", gradient = `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, count } ) => {

    const { palette, theme } = useTheme()

    const selectable = [true, false].includes( selected )

    if ( !value )
        return null

    if ( variant === "outlined" )
        return (
            <Box sx={{ cursor: onClick ? "pointer" : "default", ...containerStyles }} onClick={onClick} width="fit-content" display="inline-block" position="relative">
                <CustomTooltip disableInteractive placement="top" sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={selectable ? "button" : "div"}
                        type='button'
                        display="flex"
                        width="fit-content"
                        gap="5px"
                        alignItems="center"
                        padding="0px 5px"
                        borderRadius="10px"
                        color={color ? palette[color].main : palette.text.primary}
                        fontSize="12px"
                        sx={{
                            ":hover": {
                                boxShadow: selectable && !selected ? `0 1px 3px rgba(60, 64, 67, 0.3)` : "none",
                            },
                            ":focus": {
                                outline: selected ? `2px solid ${color ? palette[color].main : palette.text.primary}` : `1px solid ${palette.text.secondary}`,
                                outlineOffset: "1px",
                            },
                            cursor: onClick ? "pointer" : "default",
                            border: `1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`,
                            margin: "2px",
                            outlineOffset: "2px",
                            outline: selectable && selected ? `2px solid ${color ? palette[color].main : palette.text.primary}` : "none",
                            ...chipStyles
                        }}
                    >
                        {startIcon || selected ? Icons.default.CheckCircleIcon : ""}
                        <Typography
                            color={color ? palette[color].main : palette.text.primary}
                            fontSize="12px"
                            variant={textVariant || 'caption'}
                            sx={textStyles}
                        >
                            {value}
                        </Typography>
                        {count && <Typography
                            color={color ? palette[color].main : palette.text.primary}
                            fontSize="10px"
                            borderRadius="50px"
                            minWidth="20px"
                            margin="2px 0 2px 0"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            border={`1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`}
                            variant={textVariant || 'caption'}
                        >
                            {count}
                        </Typography>}
                        {endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )

    if ( variant === "semifilled" )
        return (
            <Box sx={{ cursor: onClick ? "pointer" : "default", ...containerStyles }} onClick={onClick} width="fit-content" display="inline-block" position="relative">
                <CustomTooltip disableInteractive placement="top" sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={selectable ? "button" : "div"}
                        type='button'
                        display="flex"
                        width="fit-content"
                        gap="5px"
                        alignItems="center"
                        padding="0px 5px"
                        borderRadius="10px"
                        border={`1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`}
                        bgcolor={color ? palette[color].light + 22 : theme === 'dark' ? palette.grey[700] : palette.grey[400]}
                        color={color ? palette[color].main : palette.text.primary}
                        fontSize="12px"
                        sx={{
                            ":hover": {
                                boxShadow: selectable && !selected ? `0 1px 3px rgba(60, 64, 67, 0.3)` : "none",
                            },
                            ":focus": {
                                outline: `1px solid ${palette.text.secondary}`,
                                outlineOffset: "1px",
                            },
                            cursor: onClick ? "pointer" : "default",
                            border: `1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`,
                            margin: "2px",
                            outlineOffset: "2px",
                            outline: selectable && selected ? `2px solid ${color ? palette[color].main : palette.text.primary}` : "none",
                            ...chipStyles
                        }}
                    >
                        {startIcon}
                        <Typography
                            color={color ? palette[color].main : palette.text.primary}
                            fontSize="12px"
                            variant={textVariant || 'caption'}
                            sx={textStyles}
                        >
                            {value}
                        </Typography>
                        {count && <Typography
                            color={color ? palette[color].main : palette.text.primary}
                            fontSize="10px"
                            borderRadius="50px"
                            minWidth="20px"
                            margin="2px 0 2px 0"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            border={`1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`}
                            variant={textVariant || 'caption'}
                        >
                            {count}
                        </Typography>}
                        {endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )

    if ( variant === "filled" )
        return (
            <Box sx={{ cursor: onClick ? "pointer" : "default", ...containerStyles }} onClick={onClick} width="fit-content" display="inline-block" position="relative">
                <CustomTooltip disableInteractive placement={tooltipPlacement} sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={selectable ? "button" : "div"}
                        type='button'
                        display="flex"
                        width="fit-content"
                        gap="5px"
                        alignItems="center"
                        padding="0px 5px"
                        borderRadius="10px"
                        border={`1px solid ${color ? palette[color].main + "55" : theme === 'dark' ? palette.grey[700] : palette.grey[400]}`}
                        bgcolor={color ? palette[color].main : theme === 'dark' ? palette.grey[700] : palette.grey[400]}
                        color={color ? palette[color].contrastText : palette.text.primary}
                        fontSize="12px"
                        sx={{
                            ":hover": {
                                boxShadow: selectable && !selected ? `0 1px 3px rgba(60, 64, 67, 0.3)` : "none",
                            },
                            ":focus": {
                                outline: `1px solid ${palette.text.secondary}`,
                                outlineOffset: "1px",
                            },
                            cursor: onClick ? "pointer" : "default",
                            border: `1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`,
                            margin: "2px",
                            outlineOffset: "2px",
                            outline: selectable && selected ? `2px solid ${color ? palette[color].main : palette.text.primary}` : "none",
                            ...chipStyles
                        }}
                    >
                        {startIcon}
                        <Typography
                            color={color ? palette[color].contrastText : palette.text.primary}
                            fontSize="12px"
                            variant={textVariant || 'caption'}
                            sx={textStyles}
                        >
                            {value}
                        </Typography>
                        {count && <Typography
                            color={color ? palette[color].main : palette.text.primary}
                            fontSize="10px"
                            borderRadius="50px"
                            minWidth="20px"
                            margin="2px 0 2px 0"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            border={`1px solid ${color ? palette[color].main + "55" : palette.common.font + "55"}`}
                            variant={textVariant || 'caption'}
                        >
                            {count}
                        </Typography>}
                        {endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )
    if ( variant === "gradient" )
        return (
            <Box sx={{ cursor: onClick ? "pointer" : "default", ...containerStyles }} width="fit-content" display="inline-block" position="relative">
                <CustomTooltip disableInteractive placement={tooltipPlacement} sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={selectable ? "button" : "div"}
                        type='button'
                        display="flex"
                        width="fit-content"
                        gap="5px"
                        alignItems="center"
                        padding="0px 5px"
                        borderRadius="10px"
                        border={`1px solid ${palette[color] ? palette[color].main + "55" : theme === 'dark' ? palette.grey[700] : palette.grey[400]}`}
                        sx={{ background: gradient, ...( chipStyles || {} ) }}
                        color={palette[color] ? palette[color].contrastText : color || palette.text.primary}
                        fontSize="12px"
                    >
                        {startIcon}
                        <Typography
                            color={palette[color] ? palette[color].contrastText : color || palette.text.primary}
                            fontSize="12px"
                            variant={textVariant || 'caption'}
                            sx={textStyles}
                        >
                            {value}
                        </Typography>
                        {endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )
}

export default CustomChip
